import { c as _c } from "react/compiler-runtime";
import { BasicStore, DetailedStore, type DetailedStoreWithDistance } from '@spa/components/storefinder/interfaces';
import { useSelector, useStore } from 'react-redux';
import { ClientSessionsData } from '@spa/lib/auth/sessionTypes';
import { GeolocationByTextResults } from '@spa/lib/graphql/geolocation/getGeolocationByText';
import { RootState } from '@spa/lib/store/RootState';
import { Session } from 'next-auth';
import { accountFavoriteStoreReceived } from '../../store/session/sessionSlice';
import { changeCurrentCartSelectedStoreRequested } from '../../store/carts/cartsSlice';
import generateActionId from '../../generateActionId';
import getClosestDetailedStoresByGeoLocationAction from '@spa/lib/actions/stores/getClosestDetailedStoresByGeoLocationAction';
import getGeolocationByTextAction from '@spa/lib/actions/geolocation/getGeolocationByTextAction';
import { getSortedInitialDetailedStores } from '@spa/components/storefinder/storefinder.utils';
import getStoreByIdAction from '@spa/lib/actions/stores/getStoreByIdAction';
import getStoreStockForProductAction from '@spa/lib/actions/stores/getStoreStockForProductAction';
import getStoresAction from '@spa/lib/actions/stores/getStoresAction';
import getStoresByIdsAction from '@spa/lib/actions/stores/getStoresByIdsAction';
import selectCollectAtStoreCart from '../../store/carts/selectors/selectCollectAtStoreCart';
import setMyStoreIdAction from '@spa/lib/actions/stores/setMyStoreIdAction';
import useAccessToken from '@spa/lib/react/hooks/useAccessToken';
import { useCallback } from 'react';
import useLocale from '@spa/lib/react/hooks/useLocale';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import { useSession } from 'next-auth/react';
import { useVisitorSession } from '@spa/lib/react/context/VisitorSessionContext';
import withSessionPreferences from '@spa/lib/auth/withSessionPreferences';
export interface UseStoresAPIResult {
  getGeolocation: (searchQuery: string) => Promise<GeolocationByTextResults>;
  getStores: () => Promise<ReadonlyArray<BasicStore>>;
  getClosestDetailedStores: (latitude: number, longitude: number, maximumRadius?: number, maximumResults?: number) => Promise<ReadonlyArray<DetailedStoreWithDistance>>;
  getStoreById: (storeId: string) => Promise<DetailedStore>;
  getStoresByIds: (storeIds: Array<string>) => Promise<ReadonlyArray<DetailedStore>>;
  getStoreStockIndicatorForProduct: (productCode: string, storeCode: string) => Promise<boolean>;
  getInitialDetailedStores: (allStores: ReadonlyArray<BasicStore>, limit?: number) => Promise<ReadonlyArray<DetailedStoreWithDistance>>;
  setMyStoreId: (myStoreId: string, updateSession?: boolean, newToken?: string) => Promise<void>;
  updateFavoriteStore: (refreshedSession: Session | null) => Promise<void>;
  getMyStoreId: () => string | null;
}
export function useStoresAPI() {
  const $ = _c(23);
  const accessToken = useAccessToken();
  const locale = useLocale();
  const collectAtStoreCart = useSelector(selectCollectAtStoreCart);
  const {
    session
  } = useNextAuthOrSession();
  const {
    update
  } = useSession();
  const store = useStore();
  const visitorSession = useVisitorSession();
  let t0;
  if ($[0] !== collectAtStoreCart || $[1] !== store) {
    t0 = storeCode => {
      if (collectAtStoreCart !== null && collectAtStoreCart.deliveryAddress?.storeCode !== storeCode) {
        store.dispatch(changeCurrentCartSelectedStoreRequested({
          actionId: generateActionId(),
          storeCode
        }));
      }
    };
    $[0] = collectAtStoreCart;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const syncCartStoreCode = t0;
  const getGeolocation = _temp;
  const getStores = _temp2;
  const getClosestDetailedStores = _temp3;
  const getStoreById = _temp4;
  const getStoresByIds = _temp5;
  const getStoreStockIndicatorForProduct = _temp6;
  let t1;
  if ($[3] !== locale) {
    t1 = async (allStores, limit) => await getSortedInitialDetailedStores(allStores, locale, getStoresByIds, limit);
    $[3] = locale;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getInitialDetailedStores = t1;
  let t2;
  if ($[5] !== accessToken || $[6] !== syncCartStoreCode || $[7] !== update || $[8] !== visitorSession) {
    t2 = async (myStoreId, t3, newToken) => {
      const updateSession = t3 === undefined ? true : t3;
      const isAuthenticated = Boolean(newToken || accessToken);
      if (updateSession) {
        visitorSession.setContent(withSessionPreferences(visitorSession.requireContent(), {
          favoriteStoreId: myStoreId
        }), true);
      }
      if (isAuthenticated) {
        await Promise.all([setMyStoreIdAction("setMyStoreIdAction", myStoreId), update({
          updateMeData: true
        })]);
      }
      syncCartStoreCode(myStoreId);
    };
    $[5] = accessToken;
    $[6] = syncCartStoreCode;
    $[7] = update;
    $[8] = visitorSession;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const setMyStoreId = t2;
  let t3;
  if ($[10] !== session.favoriteStoreId || $[11] !== setMyStoreId || $[12] !== store || $[13] !== syncCartStoreCode || $[14] !== visitorSession) {
    t3 = async refreshedSession => {
      const userStoreId = refreshedSession?.user.myStoreId ?? null;
      const cookieStoreId = session.favoriteStoreId;
      if (refreshedSession !== null && userStoreId && cookieStoreId !== userStoreId) {
        visitorSession.setContent(withSessionPreferences(visitorSession.requireContent(), {
          favoriteStoreId: userStoreId
        }), true);
        store.dispatch(accountFavoriteStoreReceived({
          ownerUserId: refreshedSession.user.id,
          favoriteStoreId: userStoreId
        }));
        syncCartStoreCode(userStoreId);
      } else {
        if (!userStoreId && cookieStoreId && refreshedSession?.accessToken) {
          await setMyStoreId(cookieStoreId, false, refreshedSession.accessToken);
        }
      }
    };
    $[10] = session.favoriteStoreId;
    $[11] = setMyStoreId;
    $[12] = store;
    $[13] = syncCartStoreCode;
    $[14] = visitorSession;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const updateFavoriteStore = t3;
  let t4;
  if ($[16] !== session.favoriteStoreId) {
    t4 = () => session.favoriteStoreId;
    $[16] = session.favoriteStoreId;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const getMyStoreId = t4;
  let t5;
  if ($[18] !== getInitialDetailedStores || $[19] !== getMyStoreId || $[20] !== setMyStoreId || $[21] !== updateFavoriteStore) {
    t5 = {
      getGeolocation,
      getStores,
      getClosestDetailedStores,
      getStoreById,
      getStoresByIds,
      getStoreStockIndicatorForProduct,
      getInitialDetailedStores,
      setMyStoreId,
      updateFavoriteStore,
      getMyStoreId
    };
    $[18] = getInitialDetailedStores;
    $[19] = getMyStoreId;
    $[20] = setMyStoreId;
    $[21] = updateFavoriteStore;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}
async function _temp6(productCode, storeCode_0) {
  return await getStoreStockForProductAction("getStoreStockForProductAction", productCode, storeCode_0);
}
async function _temp5(storeIds) {
  return await getStoresByIdsAction("getStoresByIdsAction", storeIds);
}
async function _temp4(storeId) {
  return await getStoreByIdAction("getStoreByIdAction", storeId);
}
async function _temp3(latitude, longitude, maximumRadius, maximumResults) {
  return await getClosestDetailedStoresByGeoLocationAction("getClosestDetailedStoresByGeoLocationAction", latitude, longitude, maximumRadius, maximumResults);
}
async function _temp2() {
  return await getStoresAction("getStoresAction");
}
async function _temp(searchQuery) {
  return getGeolocationByTextAction("getGeolocationByTextAction", searchQuery);
}